import { faArrowLeftLong, faArrowsSpin, faArrowUpShortWide, faBoxArchive, faCoins, faObjectGroup, faPrint } from '@fortawesome/free-solid-svg-icons';
import {
  faBoxOpen,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _isEmpty from 'lodash/isEmpty';
import _map from 'lodash/map';
import _uniq from 'lodash/uniq';
import PropTypes from 'prop-types';
import logoImg from 'public/static/icon-192';
import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import BarcodeQrCodeOutput from 'src/components/BarcodeQrCodeOutput';
import PrinterTypeIcon from 'src/components/icons/PrinterTypeIcon';
import IntlDate from 'src/components/intl-date';
import Loader from 'src/components/loader';
import { getMoreItemsLabel, MATERIALS_SHOW_COUNT } from 'src/components/TruncatedItemsList';
import useMediaQuery from 'src/hooks/useMediaQuery';
import { api, fetchDataByUris } from 'src/utils/api';
import config from 'src/utils/config';
import {
  API_RESOURCES,
  BUREAU_BARCODE_FORMAT,
  MATERIAL_CONTAINER_STATUSES,
  PAGINATION_IGNORE_DEFAULT_LIMIT,
} from 'src/utils/constants';
import { formatUnits } from 'src/utils/conversions';
import routes from 'src/utils/routes';
import useQueryParams, { getQRAppUri, getRouteURI, getShortUuid, getUuid } from 'src/utils/url';

const InfoRow = ({ icon, customIcon, label, value }) => (
  <div className="w-full text-left">
    <div className="text-break">
      {icon && <FontAwesomeIcon icon={icon} className='mr3' />}
      {customIcon}
      <span className='text-nowrap mr10 ml5'>{label}</span>
      {value !== null && value !== undefined && <span className='ml3'>{value}</span>}
    </div>
  </div>
);

const PRINTER_TYPES_ICON = (
  <PrinterTypeIcon size="1.25em" className="mr3" style={{ verticalAlign: '-0.125em' }} />
);

const ContainersQRCodeTraveler = () => {
  const [printer, setPrinter] = useState(null);
  const [printerType, setPrinterType] = useState(null);
  const [printerLocation, setPrinterLocation] = useState(null);
  const [scanningMode, setScanningMode] = useState(BUREAU_BARCODE_FORMAT.QR);
  const [containers, setContainers] = useState([]);
  const [lots, setLots] = useState({});
  const [batchMaterialLotUUID, setBatchMaterialLotUUID] = useState(null);
  const [batchMaterialLots, setBatchMaterialLots] = useState(null);
  const [printerTypes, setPrinterTypes] = useState([]);
  const [materials, setMaterials] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [batch, setBatch] = useState(null);
  const [userLocale, setUserLocale] = useState('en-US');
  const [bureauLogoSrc, setBureauLogoSrc] = useState(null);
  const [users, setUsers] = useState([]);
  const {
    forResource,
    backUri,
    allContainers,
    disposableContainers,
    isBatch: isBatchParam,
  } = useQueryParams();
  const isBatch = isBatchParam === 'true' || isBatchParam === true;
  const isMobileDevice = useMediaQuery('(min-width: 300px) and (max-width: 1024px)');

  const navigate = useNavigate();

  // Redirect to the initial URL or to the Scan page
  const handleRedirectBack = () => navigate(backUri || getRouteURI(routes.scan), { replace: true });

  const handlePrint = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.print();
      });
    });
  };

  const redirectToFlowsPrinterType = (printerTypeUri) => {
    if (!printerTypeUri) return null;
    return window.open(`${config.rapidfabHost}/#/assets/printer-types/?uuid=${getUuid(printerTypeUri)}`, '_blank');
  };

  // Material Container can be Permanent Container as well
  const availableResources = [API_RESOURCES.MATERIAL_CONTAINER, API_RESOURCES.MATERIAL_BATCH, API_RESOURCES.PRINTER];

  const identifyResourceToFetch = () => {
    if (!forResource) return null;
    // Identify which original resource we need to fetch the information for
    return availableResources.find(resource => forResource.includes(resource));
  };

  const fetchNonEmptyContainers = async (containers) => {
    const { EMPTY, ...containerStatusesExceptEmpty } = MATERIAL_CONTAINER_STATUSES;

    const additionalParams = {
      'filter[status]': Object.values(containerStatusesExceptEmpty).join(','),
      'page[limit]': PAGINATION_IGNORE_DEFAULT_LIMIT,
    };

    const containersByUri = await fetchDataByUris(
      API_RESOURCES.MATERIAL_CONTAINER,
      containers,
      additionalParams
    );

    if (containersByUri) {
      const nonEmptyContainers = Object.values(containersByUri);
      setContainers(nonEmptyContainers);
      return nonEmptyContainers;
    }

    return null;
  };

  async function fetchData() {
    try {
      const result = await api.get(`${API_RESOURCES.USERS}/`).json();
      setUsers(result?.resources || []);
    } catch (err) {
      console.error(err);
    }
  }

  const fetchMaterialLotAndMaterialsData = async (containers) => {
    try {
      const containerLotsUris = containers.map(container => container.material_lot).filter(Boolean);
      const containerMaterialsUris = containers.map(container => container.material).filter(Boolean);

      const [materialLotsByUri, materialsByUri] = await Promise.all([
        fetchDataByUris(API_RESOURCES.MATERIAL_LOT, containerLotsUris),
        fetchDataByUris(API_RESOURCES.MATERIAL, containerMaterialsUris),
      ]);

      if (materialLotsByUri) {
        setLots(materialLotsByUri);
      }

      if (materialsByUri) {
        setMaterials(materialsByUri);
      }
    } catch (error) {
      console.error(error);
      setError(error.message || String(error));
    }
  };


  // Printer Type History: every printer type the batch material has been exposed to.
  // The API returns URIs only (names are search-only on the backend), so they are resolved here.
  const fetchPrinterTypeHistory = async (batchData) => {
    const printerTypeUris = batchData?.printer_type_relationships;

    if (_isEmpty(printerTypeUris)) {
      setPrinterTypes([]);
      return;
    }

    try {
      const printerTypesByUri = await fetchDataByUris(API_RESOURCES.PRINTER_TYPE, printerTypeUris);
      setPrinterTypes(Object.values(printerTypesByUri ?? {}));
    } catch (error) {
      console.error(error);
      setPrinterTypes([]);
    }
  };

  const fetchMaterialBatchData = async () => {
    try {
      const batchData = await api.get(`${API_RESOURCES.MATERIAL_BATCH}/${getUuid(forResource)}/`).json();
      setBatch(batchData);

      await fetchPrinterTypeHistory(batchData);

      if (!_isEmpty(batchData.material_lots)) {
        const materialLotUris = batchData.material_lots;
        const materialLotsByUri = await fetchDataByUris(API_RESOURCES.MATERIAL_LOT, materialLotUris);

        if (materialLotsByUri) {
          setBatchMaterialLots(Object.values(materialLotsByUri));
        }
      }

      const batchContainersUris = batchData?.containers;

      if (!_isEmpty(batchContainersUris)) {
        const fetchedContainers = await fetchNonEmptyContainers(batchContainersUris);
        await fetchMaterialLotAndMaterialsData(fetchedContainers);

        return fetchedContainers;
      }
    } catch (error) {
      console.error(error);
      setError(error.message || String(error));
    }

  };

  const fetchPrinterData = async () => {
    try {
      const printer = await api.get(`${API_RESOURCES.PRINTER}/${getUuid(forResource)}/`).json();
      const printerType = printer?.printer_type && await api.get(`${API_RESOURCES.PRINTER_TYPE}/${getUuid(printer.printer_type)}/`).json();
      const printerMaterialsUris = printerType?.materials;
      const printerLocation = printer?.location && await api.get(`${API_RESOURCES.LOCATION}/${getUuid(printer.location)}/`).json();

      if (!_isEmpty(printerMaterialsUris)) {
        const materialsByUri = await fetchDataByUris(API_RESOURCES.MATERIAL, printerMaterialsUris);

        if (materialsByUri) {
          setMaterials(materialsByUri);
        }
      }

      setPrinter(printer);
      setPrinterType(printerType);
      setPrinterLocation(printerLocation);
    } catch (error) {
      console.error(error);
      setError(error.message || String(error));
    }
  };

  const fetchDisposableContainers = async () => {
    // As we pass the "UUIDs", we need to transform to the actual URIs
    const transformedDisposableContainerUris = disposableContainers.split(',').map(containerUUID =>
      `${config.apiHost}/${API_RESOURCES.MATERIAL_CONTAINER}/${containerUUID}/`
    );
    try {
      const { resources: containers } = await api.get(`${API_RESOURCES.MATERIAL_CONTAINER}/`, {
        searchParams: {
          'filter[uri]': transformedDisposableContainerUris.join(','),
        },
      }).json();

      if (!_isEmpty(containers)) {
        setContainers(containers);
        await fetchMaterialLotAndMaterialsData(containers);
      }
    } catch (error) {
      console.error(error);
      setError(error.message || String(error));
    }

  };

  const fetchMaterialContainerData = async () => {
    if (!_isEmpty(disposableContainers) && allContainers) {
      return await fetchDisposableContainers();
    }

    try {
      const container = await api.get(`${API_RESOURCES.MATERIAL_CONTAINER}/${getUuid(forResource)}/`).json();

      if (!_isEmpty(container)) {
        // If the container does not have a material lot, fetch the Current Batch (if available)
        // And set the first Material Lot from the Batch
        if (!container.material_lot && container.current_batch) {
          const batch = await api.get(`${API_RESOURCES.MATERIAL_BATCH}/${getUuid(container.current_batch)}/`).json();
          const batchMaterialLots = batch?.material_lots;
          // If the Batch has Material Lots, set the Short UUID of the first instead
          if (!_isEmpty(batchMaterialLots)) {
            setBatchMaterialLotUUID(batchMaterialLots[0].name);
          }
        }
        setContainers([container]);
        await fetchMaterialLotAndMaterialsData([container]);
      }
    } catch (error) {
      console.error(error);
      setError(error.message || String(error));
    }

  };

  const fetchResourceData = async () => {
    const resourceToFetch = identifyResourceToFetch();

    if (!resourceToFetch) return;

    switch (resourceToFetch) {
      case API_RESOURCES.MATERIAL_CONTAINER:
        await fetchMaterialContainerData();
        break;
      case API_RESOURCES.MATERIAL_BATCH:
        await fetchMaterialBatchData();
        break;
      case API_RESOURCES.PRINTER:
        await fetchPrinterData();
        break;
      default:
        break;
    }
  };

  const resolveBureauLogo = async (logoUri) => {
    if (!logoUri) {
      return null;
    }

    // Some bureau logos are returned as /document/<uuid>/ links.
    // In this case we need to fetch the document and use its upload URL.
    if (logoUri.includes(`/${API_RESOURCES.DOCUMENT}/`)) {
      try {
        const document = await api.get(`${API_RESOURCES.DOCUMENT}/${getUuid(logoUri)}/`).json();
        return document?.upload_location || logoUri;
      } catch (error) {
        console.error(error);
        return logoUri;
      }
    }

    return logoUri;
  };

  const getBureauSettings = async () => {
    try {
      const bureauSettingsResponse = await api.get(`${API_RESOURCES.BUREAU_SETTINGS}`).json();
      const bureauBrandingResponse = await api.get(`${API_RESOURCES.BUREAU_BRANDING}`).json();
      const bureauBarcodeFormatSettings = bureauSettingsResponse?.resources?.[0].barcode_default_format;
      if (bureauBarcodeFormatSettings) {
        setScanningMode(bureauBarcodeFormatSettings);
      }
      const branding = bureauBrandingResponse?.resources?.[0] || null;
      const brandingLogoUri = branding?.printable_logo || branding?.logo || null;
      const resolvedLogo = await resolveBureauLogo(brandingLogoUri);

      setBureauLogoSrc(resolvedLogo);
    } catch (error) {
      console.error(error);
      setError(error.message || String(error));
    }
  };

  const onInitialize = async () => {
    setLoading(true);
    await getBureauSettings();
    await fetchResourceData();
    await fetchData();
    setLoading(false);
  };

  useEffect(() => {
    onInitialize();
  }, []);

  useEffect(() => {
    api
      .get(API_RESOURCES.BUREAU_SETTINGS)
      .json()
      .then(result => {
        setUserLocale(result.resources[0].default_currency === 'USD' ? 'en-US' : 'en-GB');
      })
      .catch(error => {
        setUserLocale('en-US');
        console.error(error);
      });
  }, []);

  const bureauLogo = bureauLogoSrc;

  const renderFooter = ({ logoImg, bureauLogo }) => (
    bureauLogo ? (
      <p className="bureau-logo-footer">
        <img className="custom-logo" src={bureauLogo} alt="Bureau Logo" />
      </p>
    ) : (
      <p>
        Powered by <img className="logo" src={logoImg} alt="Powered by Authentise" /> Authentise
      </p>
    )
  );

  const renderContainerInfo = (container) => {
    const containerCreator = users.find(u => u.uri === container.user)?.username
    return (
      <div key={container.uri} className="traveler-secondary">
        <BarcodeQrCodeOutput
          url={getQRAppUri(container.uri)} scanningMode={scanningMode}
          className="qr-code-initial hide-on-print" />

        <div className="print-general-container">
          <div className="print-info print-info-general">

            <div className="print-block">
              <div className="print-title">Material Container:{' '}
                <span className="print-description">
                  {container.disposable
                    ? getShortUuid(container.uri)
                    : `${container.name} (${getShortUuid(container.uri)})`}
                </span>
              </div>
            </div>


            <div className="print-block">
              <div className="print-title">Material Type:{' '}
                <span className="print-description">{materials[container.material]?.name || batchMaterialLotUUID || 'N/A'}</span>
              </div>
            </div>


            <BarcodeQrCodeOutput
              url={getQRAppUri(container.uri)} scanningMode={scanningMode}
              className="qr-code-printable only-printable" />

            <div className="print-block">
              <div className="print-title">Material Lot:{' '}
                <span
                  className="print-description"
                >{lots[container.material_lot]?.name || 'N/A'}
                </span>
              </div>
            </div>

            <div className="print-block">
              <div className="print-title">Creator:{' '}
                <span
                  className="print-description"
                >{containerCreator || 'N/A'}
                </span>
              </div>
            </div>

            <div className="print-block">
              <div className="mt15 print-title">Created:{' '}
                <span className="mt15 print-description">{<IntlDate
                  withTime disableSeconds date={container.created}
                  locale={userLocale} /> || 'N/A'}
                </span>
              </div>
            </div>

          </div>

          <footer className="print-general-footer">
            {renderFooter({ logoImg, bureauLogo })}
          </footer>
        </div>

        <footer className="print-general-footer-mobile">
          {renderFooter({ logoImg, bureauLogo })}
        </footer>
      </div>
    )
  };

  const renderBatchInfo = (batch, fetchedContainers) => {
    /* eslint-disable camelcase */
    if (!batch) return null;

    const {
      uri,
      name,
      containers,
      materials = [],
      quantity,
      units,
      powder_quality,
      usage_cycles,
      created,
      user,
    } = batch;

    const selectedContainerNames = fetchedContainers
      ?.filter((container) => containers.includes(container.uri))
      .map((container) => container.name)
      .filter(Boolean)
      .join(", ") || "";

    const containerValue = selectedContainerNames.length
      ? selectedContainerNames
      : containers?.length
        ? containers.map((c) => getShortUuid(c)).join(", ")
        : "N/A";

    const batchCreator = users.find(u => u.uri === user)?.username

    const batchMaterialNames = _uniq(_map(materials, 'name'));
    const batchMaterialLotNames = _uniq(_map(batchMaterialLots, 'name'));
    // deduped by name: the backend dedupes by printer-type uuid, so two distinct records
    // sharing a name would otherwise be listed twice
    const printerTypeNames = _uniq(_map(printerTypes, 'name').filter(Boolean));

    const qrCode = (
      <BarcodeQrCodeOutput
        url={getQRAppUri(uri)}
        scanningMode={scanningMode}
        className="qr-code-hybrid"
      />
    );

    return (
      <div key={uri} className="traveler-secondary">
        {!isMobileDevice && qrCode}

        <div className="sticker">
          <InfoRow icon={faBoxArchive} label="Batch:" value={name || getShortUuid(uri)} />
          <InfoRow icon={faCoins} label="Containers:" value={containerValue} />
          {isMobileDevice && qrCode}
          <InfoRow
            icon={faArrowUpShortWide}
            label="Quantity:"
            value={`${quantity} ${formatUnits(units)}`}
          />
          <InfoRow
            label="Powder Quality:"
            value={powder_quality ? `${powder_quality.charAt(0).toUpperCase()}${powder_quality.slice(1)}` : 'N/A'}
          />
          <InfoRow icon={faArrowsSpin} label="Usage Cycles:" value={usage_cycles ?? 'N/A'} />
          <InfoRow
            icon={faObjectGroup}
            label="Material:"
            value={
              batchMaterialNames.length ?
                batchMaterialNames.map(name => name).join(', ')
                : 'N/A'
            }
          />
          <InfoRow
            keepInline
            icon={faBoxOpen}
            label="Material Lots:"
            value={
              batchMaterialLotNames.length
                ? batchMaterialLotNames.join(', ')
                : 'N/A'
            }
          />
          <InfoRow
            customIcon={PRINTER_TYPES_ICON}
            label="Printer Type History:"
            value={printerTypeNames.length ? printerTypeNames.join(', ') : 'N/A'}
          />
          <InfoRow
            label="Creator:"
            value={batchCreator || 'N/A'}
          />
          <InfoRow
            label="Created:"
            value={<IntlDate withTime disableSeconds date={created} locale={userLocale} />}
          />

          <footer className="print-general-footer">{renderFooter({ logoImg, bureauLogo })}</footer>
        </div>

        <footer className="print-general-footer-mobile">{renderFooter({ logoImg, bureauLogo })}</footer>
      </div>
    );
  };


  const renderPrinterInfo = (printer) => {
    const materialValues = Object.values(materials);

    return (
      <div key={printer.uri} className="traveler-secondary">
        <BarcodeQrCodeOutput
          url={getQRAppUri(printer.uri)} scanningMode={scanningMode}
          className="qr-code-initial hide-on-print" />

        <div className="print-general-container">
          <div className="print-info print-info-general">

            <div className="print-block">
              <div className="print-title">Printer Type:{' '}
                <span className="print-description">
                  {printerType?.name || 'N/A'}
                </span>
              </div>
            </div>


            <div className="print-block">
              <div className="print-title">Printer Name:{' '}
                <span className="print-description">{printer?.name || 'N/A'}</span>
              </div>
            </div>


            <BarcodeQrCodeOutput
              url={getQRAppUri(printer.uri)} scanningMode={scanningMode}
              className="qr-code-printable only-printable" />

            <div className="print-block">
              <div className="print-title">Location:{' '}
                <span className="print-description">{printerLocation?.name || 'N/A'}</span>
              </div>
            </div>

            <div className="print-block">
              <div className="print-title">Material Types:{' '}
                <span className="print-description">
                  {/* For a full list of material types please navigate to the printer type page in Flows */}
                  {!_isEmpty(materialValues) ? (
                    <>
                      <div className="non-printable printerMaterialsInfoTraveler">
                        {materialValues.slice(0, MATERIALS_SHOW_COUNT).map(material => (
                          <div key={material.uri}>{material.name}</div>
                        ))}
                        {materialValues.length > MATERIALS_SHOW_COUNT && (
                          <Button
                            variant="link"
                            className="truncated-items-more p-0"
                            onClick={() => redirectToFlowsPrinterType(printerType?.uri)}
                          >
                            {getMoreItemsLabel(materialValues.length - MATERIALS_SHOW_COUNT)}
                          </Button>
                        )}
                      </div>
                      <div className="only-printable">
                        {materialValues.length}
                      </div>
                    </>
                  ) : (
                    'N/A'
                  )}
                </span>
              </div>
              <div className="only-printable">For a full list, please navigate to the printer
                type page in Flows
              </div>
            </div>

          </div>

          <footer className="print-general-footer">
            {renderFooter({ logoImg, bureauLogo })}
          </footer>
        </div>

        <footer className="print-general-footer-mobile">
          {renderFooter({ logoImg, bureauLogo })}
        </footer>
      </div>
    );
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return <div>Error: {String(error)}</div>;
  }

  if (_isEmpty(containers) && !printer && !batch) {
    return <div>No containers found</div>;
  }

  return (
    <div>
      <div className="floating-button-container hide-on-print">
        <div className="floating-button floating-button-skip-animation" onClick={handleRedirectBack}>
          <FontAwesomeIcon icon={faArrowLeftLong} />
        </div>
      </div>

      <div className="floating-button-container-right hide-on-print">
        <div className="floating-button floating-button-skip-animation" onClick={handlePrint}>
          <FontAwesomeIcon icon={faPrint} />
        </div>
      </div>

      {printer
        ? renderPrinterInfo(printer)
        : isBatch
          ? renderBatchInfo(batch, containers)
          : containers.map(container => renderContainerInfo(container))}
    </div>
  );

};

InfoRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  icon: PropTypes.shape({}),
  customIcon: PropTypes.node,
};

export default ContainersQRCodeTraveler;
